import { useMemo } from "react"
import { transformUpholsteryCarsForCarSelector } from "@views/Upholstery/components/hooks/useFetchUpholsteryCars"
import {
  isPdmStructureV2Enabled,
  usePdmVehicleCatalogStore,
  useUpholsteryVehicleList,
} from "@views/UpholsteryV2"

/**
 * Helper function to determine if a car is classic or modern based on years
 */
const isClassicCar = (car: { years: string }): boolean => {
  if (!car.years) return true
  const yearList = car.years.split(", ").map((y: string) => parseInt(y.trim()))
  const maxYear = Math.max(...yearList.filter((y: number) => !isNaN(y)))
  return maxYear < 2020
}

/**
 * Hook to get upholstery cars transformed for use in CarSelector component
 * Returns modern cars (2020s to present) from upholstery data
 */
export function useUpholsteryCarsForCarSelector() {
  const isV2 = isPdmStructureV2Enabled()
  const upholsteryCars = useUpholsteryVehicleList()
  const catalogLoading = usePdmVehicleCatalogStore(
    (s) => s.loading && s.pickerCars.length === 0
  )

  const transformedUpholsteryCars = useMemo(() => {
    if (upholsteryCars.length > 0) {
      return transformUpholsteryCarsForCarSelector(upholsteryCars)
    }
    return []
  }, [upholsteryCars])

  const isUsingUpholsteryCars = isV2 || transformedUpholsteryCars.length > 0

  // Filter to only include modern cars (2020+)
  const modernCars = useMemo(() => {
    if (transformedUpholsteryCars.length > 0) {
      return transformedUpholsteryCars.filter((car) => !isClassicCar(car))
    }
    // V2 on: PDM is the only source — return empty while loading, no Dato fallback
    return []
  }, [transformedUpholsteryCars])

  return {
    modernCars,
    isUsingUpholsteryCars,
    upholsteryCars,
    catalogLoading: isV2 ? catalogLoading : false,
  }
}
